import React, {useContext, useEffect, useState} from "react";
import {useLocation} from "react-router-dom";
import {PageTitle} from "../../../_metronic/layout/core";
import {genericGetAll, genericDelete} from "../generic/crud";
import {ENTITY_ID_JOB_CARD} from "../generic/Constants";
import {showConfirmDialog} from "../../generic-components/ConfirmDialog";
import {showToast, TOAST_SUCCESS} from "../../utils/toast/Toast";
import {JobCardAddEditDialog} from "./JobCardAddEditDialog";
import {JobCardDetailsDialog} from "./JobCardDetailsDialog";
import TrackVASContext from "../../TrackVASContext";
import {JobCardItem} from "./JobCardItem";

export function JobSearchPage() {
    const location = useLocation();
    const queryParams = new URLSearchParams(location.search);
    const searchQuery = queryParams.get("q") || "";

    const [jobCards, setJobCards] = useState<any[]>([]);
    const [loading, setLoading] = useState(false);
    const [showModal, setShowModal] = useState(false);
    const [showModelId, setShowModelId] = useState<string | null>(null);
    const [showDetailsModal, setShowDetailsModal] = useState(false);
    const [detailsJobCardId, setDetailsJobCardId] = useState<string | null>(null);
    const [reloadCounter, setReloadCounter] = useState(0);

    const myData = useContext(TrackVASContext);

    useEffect(() => {
        // Clear toolbar buttons or set specific ones for search page
         if (myData && myData.setBaseData) {
            myData.setBaseData({
                toolBarButtons: []
            });
        }
    }, [myData]);

    useEffect(() => {
        if (searchQuery) {
            fetchJobCards(searchQuery);
        } else {
            setJobCards([]);
        }
    }, [searchQuery, reloadCounter]);

    const fetchJobCards = async (query: string) => {
        setLoading(true);
        try {
            // Search across multiple fields if possible, or just vehicleNo
            // The user requested searching for vehicle number.
            // We'll try searching by vehicleNo first.
            const response = await genericGetAll(ENTITY_ID_JOB_CARD, {
                page: 1,
                sizePerPage: 100,
                currentIndex: 0,
                searchField: 'vehicleNo', // Adjust if backend supports multiple fields
                searchString: query,
                sortBy: 'createdOn',
                sortOrder: 'desc'
            });
            
            setJobCards(response.data.entities || []);
        } catch (error) {
            console.error("Error searching job cards:", error);
            setJobCards([]);
        } finally {
            setLoading(false);
        }
    };

    const reloadData = () => {
        setReloadCounter(prev => prev + 1);
    };

    const handleDelete = async (id: string) => {
        const result = await showConfirmDialog("Do you really want to delete this Job Card?");
        if (result) {
            genericDelete(ENTITY_ID_JOB_CARD, id)
                .then(() => {
                    showToast("Job Card Deleted!", TOAST_SUCCESS);
                    reloadData();
                })
                .catch(() => {});
        }
    };

    const breadCrumbs = [
        { title: "Home", path: "/dashboard", isSeparator: false, isActive: false },
        { title: "Search Results", path: "", isSeparator: true, isActive: false },
    ];

    return (
        <>
            <PageTitle breadcrumbs={breadCrumbs}>
                {`Search Results for "${searchQuery}"`}
            </PageTitle>

            <div className="card">
                <div className="card-header border-0 pt-5">
                    <h3 className="card-title align-items-start flex-column">
                        <span className="card-label fw-bolder fs-3 mb-1">Search Results</span>
                        <span className="text-muted mt-1 fw-bold fs-7">
                            {jobCards.length} result{jobCards.length !== 1 ? 's' : ''} found
                        </span>
                    </h3>
                </div>
                <div className="card-body pt-3 pb-5">
                    {loading ? (
                        <div className="text-center py-5">
                            <span className="spinner-border text-primary"></span>
                        </div>
                    ) : jobCards.length > 0 ? (
                        <div className="row">
                            {jobCards.map(card => (
                                <JobCardItem 
                                    key={card.id} 
                                    card={card}
                                    onDetailsClick={(id) => { setDetailsJobCardId(id); setShowDetailsModal(true); }}
                                    onEditClick={(id) => { setShowModelId(id); setShowModal(true); }}
                                    onDeleteClick={(id) => handleDelete(id)}
                                />
                            ))}
                        </div>
                    ) : (
                        <div className="alert alert-light-primary d-flex align-items-center p-5 mb-0">
                            <i className="bi bi-search fs-2 text-primary me-3"></i>
                            <div className="text-primary fw-bold">
                                No job cards found matching "{searchQuery}".
                            </div>
                        </div>
                    )}
                </div>
            </div>

            <JobCardAddEditDialog
                show={showModal}
                handleClose={() => setShowModal(false)}
                id={showModelId}
                onSave={reloadData}
                preSelectedBranch={null}
            />

            <JobCardDetailsDialog
                show={showDetailsModal}
                handleClose={() => setShowDetailsModal(false)}
                jobCardId={detailsJobCardId}
                onStatusChange={reloadData}
            />
        </>
    );
}